import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import Iconify from "minimals-template/components/Iconify"
import { HighlightBox } from "lib/@components/highlightBox"
import { blockLinkButtonSx, linkedButtonSx } from "./component-link-button-styles"
import { preventPropagation } from "lib/prevent"

const FIELD = "content"
const ICON_SX = { width: 14, height: 14 }

/**
 * Slot plug for TaskContent.
 *
 * - No componentRef: renders the default content editor
 * - ComponentRef set, field not linked: puzzle icon toggles field on (if component has actionsHtml)
 * - Field linked: shows inherited actionsHtml + link icon to unlink
 */
export function ComponentRefContent({ originalchildren }) {
    const { componentRef, linkedFields, onEnableField, onDisableField, editMode, componentTask } = useBoundContext()
    const isLinked = !!componentRef && linkedFields?.[FIELD]

    if (!isLinked) {
        const canLink = !!componentRef && componentHasContent(componentTask)

        return (
            <Box sx={{ position: "relative" }}>
                {originalchildren}
                {editMode && canLink && (
                    <Box sx={{ position: "absolute", top: "50%", left: -28, transform: "translateY(-50%)", zIndex: 1 }}>
                        <Tooltip title="Link content from component">
                            <IconButton
                                size="small"
                                onClick={preventPropagation(handleEnable)}
                                className="component-link-btn"
                                sx={blockLinkButtonSx}
                                aria-label="Link content"
                            >
                                <Iconify icon="mdi:puzzle-plus-outline" sx={ICON_SX} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        )
    }

    if (componentTask === IS_LOADING) {
        return (
            <Box display="flex" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress size={20} />
            </Box>
        )
    }

    if (!componentTask) return originalchildren ?? null

    return (
        <HighlightBox path="content">
            <Tooltip title={`Inherited from: ${componentTask.heading ?? componentRef}`}>
                <Box className="styled-bullets" sx={{ opacity: editMode ? 0.6 : 1, position: "relative" }}>
                    <Box dangerouslySetInnerHTML={{ __html: componentTask.actionsHtml ?? "" }} />
                    {editMode && (
                        <Box sx={{ position: "absolute", top: 0, right: -4 }}>
                            <IconButton
                                size="small"
                                onClick={preventPropagation(handleDisable)}
                                sx={linkedButtonSx}
                                aria-label="Unlink content"
                            >
                                <Iconify icon="mdi:link-variant" sx={ICON_SX} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Tooltip>
        </HighlightBox>
    )

    function handleEnable() {
        onEnableField?.(FIELD)
    }

    function handleDisable() {
        onDisableField?.(FIELD)
    }
}

function componentHasContent(task) {
    if (!task || task === IS_LOADING) return false
    return !!task.actionsHtml
}
